import { useEffect, useMemo, useState } from 'react';
import type { User } from '@frontend/types';

export function useAdminWidgetExpanded(
  user: User | null | undefined,
  storageSuffix: string,
  defaults: Record<string, boolean>
) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(defaults);

  const persistKey = useMemo(() => {
    const userId = user?.pk || user?.username || user?.email || 'admin';
    return `admin_widget_state_${storageSuffix}_${userId}`;
  }, [user, storageSuffix]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(persistKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, boolean>;
      setExpanded((current) => ({ ...current, ...parsed }));
    } catch {
      // Ignore malformed local storage values.
    }
  }, [persistKey]);

  useEffect(() => {
    localStorage.setItem(persistKey, JSON.stringify(expanded));
  }, [expanded, persistKey]);

  const toggleWidget = (widgetId: string) => {
    setExpanded((current) => ({ ...current, [widgetId]: !current[widgetId] }));
  };

  return { expanded, setExpanded, toggleWidget };
}
